import { Module } from 'vuex'
import {RootState} from '../index'
import dayjs from 'dayjs';
import {getCltnNftGroupListApi, getSeatRefTimeApi} from '@/api/discoverApis';
import {HID_LINK, REF_CODE_STORAGE} from '@/config/constants';
import {TimeItem} from '@/models/orderModels';
export interface SubProductInfo {
  price: any;
  seat: any;
}
export interface ProductInfo {
  productId: string;
  count: number;
  subList: SubProductInfo[];
  defaultPriceId: number;
}
export interface OrderState {
  process: string[],
  cltn: any,
  qtnrTemplate: any[],
  seat: any,
  isQtnr: boolean,
  customer: any;
  selectList: ProductInfo[];
  seatRefTime: number;
  seatReqId: string;
  requested: boolean;
  crcy: string;
  ticket: {
    groupList: any[],
    date: {
      startDate: string;
      endDate: string;
      cltnTimeId?: number;
    }
  };
}

const initTicket = () => {
  return {
    selectList: [],
    groupList: [],
    date: {
      cltnTimeId: '',
      startDate: '',
      endDate: '',
    },
  }
}

const getDefaultQtnr = () => {
  return [
    {
      name: 'key:name',
      required: true
    },
    {
      name: 'key:phone',
      required: true
    }
  ]
}
export const orderModule: Module<OrderState, RootState> = {
  namespaced: true,
  state: () => ({
    process: [],
    cltn: {},
    seat: [],
    crcy: '',
    qtnrTemplate: getDefaultQtnr(),
    isQtnr: false,
    customer: null,
    selectList: [],
    ticket: initTicket(),
    seatRefTime: 0,
    seatReqId: '',
    requested: false,
  }),
  mutations: {
    INIT_TICKET: (state) => {
      state.process = []
      state.cltn = {}
      state.crcy = ''
      state.ticket = initTicket()
      state.qtnrTemplate = getDefaultQtnr()
      state.customer = null
      state.isQtnr = false
      state.selectList = []
      state.seat = []
      state.seatRefTime = 0
      state.requested = false
      state.seatReqId = ''
    },
    INIT_SEAT: (state) => {
      state.selectList = []
      state.seat = []
      state.seatRefTime = 0
      state.seatReqId = ''
    },
    COMMIT_TICKET_DATE: (state, { startDate, endDate, cltnTimeId }) => {
      state.ticket.date.startDate = startDate
      state.ticket.date.endDate = endDate
      state.ticket.date.cltnTimeId = cltnTimeId
    },
    FETCH_QTNR: (state, qtnr) => {
      state.qtnrTemplate = qtnr
      state.isQtnr = true
    },
  },
  actions: {
    updateNftGroup: async ({ state }) => {
      const cltnId = state.cltn.id
      if (!cltnId) return
      const refCode = sessionStorage.getItem(REF_CODE_STORAGE) as string
      const { list } = await getCltnNftGroupListApi(cltnId, refCode)
      state.ticket.groupList = list.map((item: any) => {
        return {
          ...item,
          maxCnt: Math.min(item.mintableCnt, item.mintableCntPerWallet - item.buyCnt)
        }
      })
    },
    setSeatRefTime: async ({ state}) => {
      if (state.seatReqId) {
        const { endTime } = await getSeatRefTimeApi(state.seatReqId)
        state.seatRefTime = endTime - (1000 * 120)
      }
    },
    getCustomerInput: async ({ state, getters }) => {
      const input = structuredClone(state.customer)
      input.isGift = getters.isGiftCondition
      return input
    }
  },
  getters: {
    getCltnId: (state) => {
      const crcy = state.crcy
      return crcy ? state.cltn.id : ''
    },
    isSelectEmpty: (state) => {
      return !state.selectList.length
    },
    getDateOptions: (state) => {
      const options: any[] = []
      const groupList = state.ticket.groupList.toSorted((a, b) => {
        return dayjs(a.attributes.startDate).isBefore(b.attributes.startDate) ? -1 : 1
      })
      if (groupList.length) {
        for (const group of groupList) {
          const att = group.attributes
          //이벤트 후 수정
          const saleEndTime = att.endDate
          if (group.mintableCnt > 0 && dayjs(saleEndTime).isAfter(dayjs())) {
            const timeItem = {
              startDate: att?.startDate,
              endDate: att?.endDate,
              cltnTimeId: group.cltnTimeId,
            }
            const findIndex = options.findIndex(({date}) => {
              if (date) {
                return dayjs(date).tz(att.timeZone).isSame(att?.startDate, 'd')
              }
              return false
            })
            if (findIndex === -1) {
              options.push({
                date: att?.startDate,
                timeZone: att?.timeZone,
                timeList: [timeItem],
              })
            } else {
              if (!options[findIndex].timeList.some((time: TimeItem) => {
                return time.startDate === timeItem.startDate && time.endDate === timeItem.endDate
              })) {
                options[findIndex].timeList.push(timeItem)
              }
            }
          }
        }
      }
      return options.sort((a, b) => {
        return dayjs(a.date).isBefore(b.date) ? -1 : 1
      })
    },
    getGroupOptions: (state) => {
      const refCode = sessionStorage.getItem(REF_CODE_STORAGE)
      const filteredList = state.ticket.groupList.filter(group => {
        const att = group.attributes
        const ticketDate = state.ticket.date
        const priceInfo = group.priceList.find((price: any) => price.currency === state.crcy)
        return att.startDate === ticketDate.startDate && att.endDate === ticketDate.endDate && priceInfo
      })
      const hasRef = refCode && !!filteredList.find(group => group.attributes?.ref === refCode)
      return filteredList.filter(group => {
        if (hasRef) {
          return group.attributes?.ref === refCode
        } else return !group.attributes?.ref
      }).map(option => {
        const priceInfo = option.priceList.find((price: any) => price.currency === state.crcy)
        return {
          ...option,
          priceId: priceInfo.id,
          price: priceInfo.price,
          dcPrice: priceInfo.dcPrice,
          priceUnit: state.crcy,
          desc: option.desc,
          disabled: option.maxCnt === 0 || option.status === 'STOP',
          subPriceUnit: state.cltn.attributes.subPriceUnit
        }
      })
    },
    getAllGroupOptions: (state) => {
      const refCode = sessionStorage.getItem(REF_CODE_STORAGE)
      const filteredList = state.ticket.groupList.filter((group) => {
        let isIn = true
        if (group.attributes?.endDate) {
          isIn = dayjs().isBefore(group.attributes.endDate)
        }
        const priceInfo = group.priceList.find((price: any) => price.currency === state.crcy)
        return priceInfo && isIn
      })
      const hasRef = refCode && !!filteredList.find(group => group.attributes?.ref === refCode)
      return filteredList.filter(group => {
        if (hasRef) {
          return group.attributes?.ref === refCode
        } else return !group.attributes?.ref
      }).map(option => {
        const priceInfo = option.priceList.find((price: any) => price.currency === state.crcy)
        return {
          ...option,
          priceId: priceInfo.id,
          price: priceInfo.price,
          dcPrice: priceInfo.dcPrice,
          priceUnit: state.crcy,
          desc: option.desc,
          disabled: option.maxCnt === 0 || option.status === 'STOP',
          subPriceUnit: state.cltn.attributes.subPriceUnit
        }
      })
    },
    getNgListForValid: (state) => {
      return state.selectList.map(select => {
        return {
          id: select.productId,
          count: select.count
        }
      })
    },
    isQtnrTemplateEmpty: (state) => {
      return !state.qtnrTemplate.length
    },
    isGiftCondition: (state) => {
      let isCond = false
      const groupList = state.ticket.groupList
      const selectList = state.selectList
      selectList.forEach(item => {
        const group = groupList.find(gp => gp.id === item.productId)
        const giftCond = group?.attributes.giftCond
        if (giftCond) {
          if (giftCond <= item.count) isCond = true
        }
      })
      return isCond
    },
  },
}
